<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title>{{ $t("profile.information.title") }}</template>

        <template #description>{{
            $t("profile.information.description")
        }}</template>

        <template #form>
            <!-- Profile Photo -->
            <div
                v-if="$page.props.jetstream.managesProfilePhotos"
                class="col-span-6"
            >
                <!-- Name -->
                <div class="gap-1 col-span-6 grid">
                    <Label for="name">
                        {{ $t("profile.information.name") }}</Label
                    >
                    <Input id="name" v-model="form.name" type="text" />

                    <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <!-- Email -->
                <div class="mt-6 gap-1 col-span-6 flex flex-col">
                    <Label for="email">
                        {{ $t("profile.information.email") }}</Label
                    >

                    <div class="relative items-center">
                        <Input
                            v-model="form.email"
                            type="email"
                            id="email"
                            class="pl-10"
                        />
                        <span
                            class="inset-y-0 start-0 px-3 absolute flex items-center justify-center"
                        >
                            <Mail class="size-6 text-iron-500" />
                        </span>
                    </div>

                    <jet-input-error
                        :message="form.errors.email"
                        class="mt-2"
                    />
                </div>

                <!-- Profile Photo File Input -->
                <input
                    ref="photo"
                    type="file"
                    class="hidden"
                    @change="updatePhotoPreview"
                />

                <div class="mt-6 gap-4 flex">
                    <div class="shrink-0">
                        <ImageZaptimeShape
                            :src="user.profile_photo_url"
                            :alt="user.name"
                            size="lg"
                        >
                        </ImageZaptimeShape>
                    </div>

                    <div
                        class="rounded-xl border-gray-200 bg-iron-50 p-4 flex w-full flex-col items-center justify-center border"
                        @dragover.prevent="dragOver = true"
                        @dragleave.prevent="dragOver = false"
                        @drop="handleDrop"
                        :class="[
                            dragOver
                                ? 'shadow-lg ring-z-primary-600 ring-2'
                                : '',
                        ]"
                    >
                        <Button
                            variant="outline"
                            size="icon"
                            class="mr-2"
                            type="button"
                            @click.prevent="selectNewPhoto"
                        >
                            <CloudUpload
                                :size="20"
                                class="text-iron-500"
                            ></CloudUpload>
                        </Button>

                        <p
                            class="mt-3 text-sm text-iron-500 max-w-[320px] text-center"
                        >
                            <Button
                                type="button"
                                @click.prevent="selectNewPhoto"
                                variant="ghost"
                            >
                                {{
                                    $t("profile.information.upload_image")
                                }}</Button
                            >
                            <span>&nbsp;</span>
                            <span
                                v-html="
                                    $t(
                                        'profile.information.upload_image_description',
                                    )
                                "
                            ></span>
                        </p>

                        <!---->
                        <!-- <Button -->
                        <!--     v-if="user.profile_photo_path" -->
                        <!--     class="mt-1 block" -->
                        <!--     variant="ghost" -->
                        <!--     @click.prevent="deletePhoto" -->
                        <!-- > -->
                        <!--     {{ $t("profile.information.remove_photo") }} -->
                        <!-- </Button> -->
                    </div>
                </div>

                <UpdatePhotoModal
                    v-model="photoModalConfirmationOpened"
                    @confirm="updateProfilePhoto"
                ></UpdatePhotoModal>
            </div>

            <div class="sm:col-span-4 col-span-6">
                <div class="mt-2 space-x-2 flex items-center">
                    <Switch
                        id="use-in-emailing"
                        :checked="useProfileImageInEmailing"
                        @update:checked="toggleUseInEmailing"
                    />
                    <Label for="use-in-emailing">{{
                        $t("profile.use_profile_image_in_emailing")
                    }}</Label>
                    <PaidFeatureIcon />
                </div>
            </div>
        </template>

        <template #actions>
            <Button
                size="sm"
                type="submit"
                :loading="form.processing"
                :recently-successful="form.recentlySuccessful"
            >
                {{ $t("save_changes") }}</Button
            >
        </template>
    </jet-form-section>
</template>

<script lang="ts" setup>
import JetFormSection from "../../Jetstream/FormSection.vue";
//@ts-expect-error
import JetInputError from "../../Jetstream/InputError.vue";

import { Button } from "@/components/ui/button";
import { CloudUpload, Mail } from "lucide-vue-next";
import { router, useForm, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { computed, ref } from "vue";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

import PaidFeatureIcon from "../Zaptime/App/Atomic/PaidFeatureIcon.vue";
import useProDialog from "@/ProModal/composables/useProDialog";
import { useHasSubscription } from "@/composables/useHasSubscription";
import ImageZaptimeShape from "@/components/ImageZaptimeShape.vue";
import UpdatePhotoModal from "./UpdatePhotoModal.vue";

import { useToast } from "@/components/ui/toast/use-toast";
import { useI18n } from "vue-i18n";

const { toast } = useToast();
const { t } = useI18n();

const props = defineProps<{
    user: any;
}>();

const photo = ref();

const dragOver = ref(false);
const photoModalConfirmationOpened = ref(false);
const fileWaitingForModalConfirm = ref<File>();

const form = useForm({
    name: props.user.name,
    email: props.user.email,
});

const photoForm = useForm<{ image: File | null; useWithEventTypes: boolean }>({
    image: null,
    useWithEventTypes: false,
});

type SharedData = {
    personalTeam: {
        id: number;
        use_profile_image_in_emailing: boolean;
    };
};

const photoPreview = ref(null);
const page = usePage<SharedData>();

const { hasSubscription } = useHasSubscription();
const proDialog = useProDialog();

const useProfileImageInEmailing = computed(() => {
    return page.props.personalTeam.use_profile_image_in_emailing;
});

function updateProfileInformation() {
    form.put(route("user-profile-information.update"), {
        errorBag: "updateProfileInformation",
        preserveScroll: true,
    });
}

function toggleUseInEmailing() {
    if (!hasSubscription) {
        proDialog.show({
            feature: "customize_confirmation_email",
        });
    } else {
        router.post(
            route("app.teams.profile-photos.use-in-emailing", {
                team: page.props.personalTeam.id,
            }),
        );
    }
}

async function updateProfilePhoto({
    withEventTypes,
}: {
    withEventTypes: boolean;
}) {
    if (!fileWaitingForModalConfirm.value) {
        return;
    }

    photoForm.image = fileWaitingForModalConfirm.value;

    photoForm.useWithEventTypes = withEventTypes;

    photoForm.post(route("app.profile-photos.store"), {
        errorBag: "updateProfileInformation",
        preserveScroll: true,
        onSuccess: () => {
            closePhotoUpdateConfirmModal();
        },
    });
}

function selectNewPhoto() {
    photo.value.click();
}

const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    dragOver.value = false;
    if (event.dataTransfer?.files.length) {
        const file = event.dataTransfer.files[0];

        // Check if file size exceeds 1MB
        if (file && file.size > 1024 * 1024) {
            toast({
                title: t("profile.image_validation.title"),
                description: t("profile.image_validation.description"),
                variant: "destructive",
            });
            return;
        }

        openPhotoUpdateConfirmModal(file);
    }
};

function openPhotoUpdateConfirmModal(file: File) {
    fileWaitingForModalConfirm.value = file;
    photoModalConfirmationOpened.value = true;
}

function closePhotoUpdateConfirmModal() {
    photoModalConfirmationOpened.value = false;
    fileWaitingForModalConfirm.value = undefined;
}

function updatePhotoPreview() {
    const reader = new FileReader();

    reader.onload = (e) => {
        //@ts-expect-error
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo.value.files[0]);

    const file = photo.value.files[0];

    // Check if file size exceeds 1MB
    if (file && file.size > 1024 * 1024) {
        toast({
            title: t("profile.image_validation.title"),
            description: t("profile.image_validation.description"),
            variant: "destructive",
        });

        photo.value.value = "";
        return;
    }

    openPhotoUpdateConfirmModal(file);
}

function deletePhoto() {
    photoForm.delete(route("app.profile-photos.destroy"), {
        preserveScroll: true,
        onSuccess: () => (photoPreview.value = null),
    });
}
</script>
